// src/pages/ResubscribePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { createUpgradeSession } from '../services/stripe.service';
import {
  Crown,
  Loader2,
  Package,
  TrendingUp,
  Receipt,
  BarChart3,
  Check,
  AlertCircle,
  ArrowRight,
  LogOut
} from 'lucide-react';
import { toast } from '../components/Toast';

const ResubscribePage = () => {
  const { currentUser, signOut } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Set page title
  useEffect(() => { document.title = "Resubscribe | Nexus"; }, []);

  const handleResubscribe = async () => {
    if (!currentUser) return;

    setCheckoutLoading(true);
    try {
      const result = await createUpgradeSession('pro', 'month');

      if (result.error) {
        toast.error(result.error);
        setCheckoutLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/dashboard');
  };

  const features = [
    { icon: Package, text: 'Unlimited inventory tracking' },
    { icon: TrendingUp, text: 'Sales & profit analytics' },
    { icon: Receipt, text: 'Expense management' },
    { icon: BarChart3, text: 'Detailed reports & insights' },
  ];

  const statusMessage = subscriptionStatus.isCanceled
    ? 'Your subscription has been canceled'
    : subscriptionStatus.isPastDue
    ? 'Your payment failed'
    : subscriptionStatus.isExpired
    ? 'Your subscription has expired'
    : 'Access Required';

  const statusDescription = subscriptionStatus.isCanceled
    ? 'Resubscribe to regain access to all your data and features.'
    : subscriptionStatus.isPastDue
    ? 'Please update your payment method to continue using Nexus.'
    : subscriptionStatus.isExpired
    ? 'Your subscription has expired. Subscribe now to continue using all features and keep your data.'
    : 'Subscribe to access all features of Nexus.';

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800">
          {/* Header */}
          <div className="relative px-8 pt-10 pb-8 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white text-center">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+PC9zdmc+')] opacity-50" />

            <div className="relative">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
                <AlertCircle className="w-8 h-8" />
              </div>

              <h1 className="text-2xl font-bold mb-2">
                {statusMessage}
              </h1>
              <p className="text-amber-100 text-sm">
                {statusDescription}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6">
            {/* Features reminder */}
            <div className="mb-6">
              <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-3">
                Get back access to:
              </p>
              <div className="space-y-2">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm text-zinc-700 dark:text-zinc-300">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-zinc-900 dark:text-zinc-100">Pro Plan</p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">Full access to all features</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">$15</p>
                  <p className="text-xs text-zinc-500">/month</p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-3">
              <button
                onClick={() => handleResubscribe()}
                disabled={checkoutLoading}
                className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold flex items-center justify-center gap-2 transition-all hover:shadow-lg disabled:opacity-50"
              >
                {checkoutLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Crown className="w-5 h-5" />
                    Resubscribe
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                onClick={handleSignOut}
                className="w-full py-2.5 px-4 rounded-xl border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 font-medium flex items-center justify-center gap-2 transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>

            {/* Benefits */}
            <div className="mt-4 flex items-center justify-center gap-4 text-xs text-zinc-500">
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-500" />
                Cancel anytime
              </span>
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-500" />
                Keep all your data
              </span>
            </div>
          </div>
        </div>

        {/* Footer link */}
        <p className="text-center text-zinc-500 text-xs mt-4">
          Questions? Contact support@nexusmanager.app
        </p>
      </div>
    </div>
  );
};

export default ResubscribePage;
